import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MessengerPageSummary } from '@/Config/api/queryClient';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';

interface MessengerPendingPagesPanelProps {
  pages: MessengerPageSummary[];
  onConfirm: (page: MessengerPageSummary) => void;
  onConnectDifferentAccount: () => void;
  onCancel?: () => void;
  confirmingPageId: string | null;
  isCancelling: boolean;
  /** A re-authorization popup is open; these Pages may still be replaced by the poll. */
  isConnecting: boolean;
  /** A Page is already live, so nothing here is the difference between working and not. */
  isConnected: boolean;
}

/** The staged Pages awaiting an explicit pick, as an alert so pending reads as needing action. */
export const MessengerPendingPagesPanel = ({
  pages,
  onConfirm,
  onConnectDifferentAccount,
  onCancel,
  confirmingPageId,
  isCancelling,
  isConnecting,
  isConnected,
}: MessengerPendingPagesPanelProps) => {
  const { t } = useTranslation(['integration']);
  const singlePage = pages.length === 1 ? pages[0] : null;

  return (
    <Alert variant="warning" data-testid="messenger-pending-panel">
      <AlertTriangle className="size-4" />
      <AlertTitle>
        {t(
          singlePage
            ? isConnected
              ? 'integration:messenger.select_page.single_title_connected'
              : 'integration:messenger.select_page.single_title'
            : 'integration:messenger.select_page.title',
        )}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-3">
        <p className="text-xs">
          {t(
            singlePage
              ? isConnected
                ? 'integration:messenger.select_page.single_description_connected'
                : 'integration:messenger.select_page.single_description'
              : 'integration:messenger.select_page.description',
          )}
        </p>

        {/* Every button in this panel is disabled while a popup is in flight; say why. */}
        {isConnecting ? (
          <p className="text-xs text-foreground-subtle" data-testid="messenger-pending-locked-hint">
            {t('integration:messenger.select_page.confirm_locked_hint')}
          </p>
        ) : null}

        {singlePage ? (
          <div className="flex flex-col gap-2" data-testid="messenger-page-selection-list">
            <div className="min-w-0">
              <p
                className="truncate text-sm text-foreground"
                data-testid={`messenger-page-option-name-${singlePage.page_id}`}
              >
                {singlePage.page_name || singlePage.page_id}
              </p>
              <p className="truncate text-xs text-foreground-subtle">{singlePage.page_id}</p>
            </div>
            <Button
              type="button"
              variant={singlePage.is_connected ? 'secondary' : 'dark'}
              size="sm"
              className="w-full min-w-0"
              onClick={() => onConfirm(singlePage)}
              disabled={confirmingPageId !== null || isConnecting}
              data-testid={`messenger-page-confirm-${singlePage.page_id}`}
            >
              {confirmingPageId === singlePage.page_id ? <Spinner className="size-4" /> : null}
              <span className="truncate">
                {singlePage.is_connected
                  ? t('integration:messenger.select_page.keep')
                  : t('integration:messenger.select_page.confirm_named', {
                      name: singlePage.page_name || singlePage.page_id,
                    })}
              </span>
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="messenger-page-selection-list">
            {pages.map((page) => (
              <div
                key={page.page_id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3"
                data-testid={`messenger-page-option-${page.page_id}`}
              >
                <div className="min-w-0">
                  <p
                    className="truncate text-sm text-foreground"
                    data-testid={`messenger-page-option-name-${page.page_id}`}
                  >
                    {page.page_name || page.page_id}
                  </p>
                  <p className="truncate text-xs text-foreground-subtle">{page.page_id}</p>
                </div>
                <Button
                  type="button"
                  variant={page.is_connected ? 'secondary' : 'dark'}
                  size="sm"
                  className="shrink-0"
                  onClick={() => onConfirm(page)}
                  disabled={confirmingPageId !== null || isConnecting}
                  data-testid={`messenger-page-confirm-${page.page_id}`}
                >
                  {confirmingPageId === page.page_id ? <Spinner className="size-4" /> : null}
                  {page.is_connected
                    ? t('integration:messenger.select_page.keep')
                    : t('integration:messenger.select_page.confirm')}
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={onConnectDifferentAccount}
            disabled={isConnecting}
            data-testid="messenger-pending-different-account"
          >
            {t('integration:messenger.select_page.different_account')}
          </Button>
          {/* Pre-connection only: switching Pages must not delete a live integration. */}
          {onCancel ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onCancel}
              disabled={isCancelling || isConnecting}
              data-testid="messenger-pending-cancel-button"
            >
              {t('integration:messenger.select_page.cancel')}
            </Button>
          ) : null}
        </div>
      </AlertDescription>
    </Alert>
  );
};
